import type {
  AgingBalance,
  AgingBalanceMonthly,
  AgingBalanceSnapshot,
  CashCollectedMonthly,
  CollectionsSummary,
  OutreachSummary,
  DsoMonthly,
  InvoiceBalanceSummary,
  TaskStatusTotal,
  TaskTypeTotal,
  AnalyticsInterval,
  InvoiceForecast,
  MonthlyAgingSnapshotsInput,
  MonthlyAgingSnapshotsOutput,
  CollectorOutreachSummaryItem,
} from '../models';
import {
  CollectorSummary,
  CollectionsSummaryByCustomerLine,
  CollectionsSummaryLine,
  CustomerForecastLine,
  OutreachSummaryLine,
  TaskSummaryLine,
  TaskAutomationBreakdownLine,
  TaskCompletionTimeByTypeLine,
} from '../models';
import { Resource } from './resource';

function buParams(businessUnitIds?: string[]): Record<string, any> {
  return businessUnitIds?.length ? { business_unit_id__in: businessUnitIds } : {};
}

export class Analytics extends Resource {
  resourcePath = 'v1/analytics';

  getReport(name: 'aging-balance'): Promise<AgingBalanceSnapshot>;
  getReport(name: 'aging-balance-by-month'): Promise<AgingBalanceMonthly>;
  getReport(name: 'historical-aging-balance-by-month'): Promise<AgingBalanceMonthly>;
  getReport(name: 'cash-collected-by-month'): Promise<CashCollectedMonthly>;
  getReport(name: 'collections-summary'): Promise<CollectionsSummary>;
  getReport(name: 'dso-by-month'): Promise<DsoMonthly>;
  getReport(name: 'invoice-balance-summary'): Promise<InvoiceBalanceSummary>;
  getReport(name: 'outreach-summary'): Promise<OutreachSummary>;
  getReport(name: string, download: boolean, allTime: boolean): Promise<any>;
  getReport(name: string, download = false, allTime = false): Promise<any> | Promise<void> {
    const headers: Record<string, any> = {};
    if (download) {
      headers.Accept = 'text/csv';
      headers['Content-Disposition'] = `attachment; filename="${name}.csv"`;
    }

    const params: Record<string, any> = {};

    if (allTime) {
      params.all_time = allTime;
    }

    return this.api
      .get(this.path(name), { headers, params })
      .then((resp) => (download ? resp : resp.data));
  }

  async getAgingBalance(businessUnitIds?: string[]): Promise<AgingBalance> {
    const res = await this.api.get(this.path('collections/aging-balance'), {
      params: { ...buParams(businessUnitIds) },
    });

    return res.data;
  }

  async getCollectorSummary(
    interval?: AnalyticsInterval,
    businessUnitIds?: string[],
  ): Promise<Record<string, CollectorSummary[]>> {
    const res = await this.api.get(this.path('collectors/summary'), {
      params: { interval, ...buParams(businessUnitIds) },
    });

    const summary: Record<string, CollectorSummary[]> = {};
    Object.entries(res.data.data).forEach(([key, items]) => {
      if (!(key in summary)) {
        summary[key] = [];
      }

      summary[key].push(
        ...(items as CollectorSummary[]).map((item) => CollectorSummary.fromJson(item)),
      );
    });

    return summary;
  }

  async getCollectionsSummary(
    interval?: AnalyticsInterval,
    businessUnitIds?: string[],
  ): Promise<CollectionsSummaryLine[]> {
    const res = await this.api.get(this.path('collections/summary'), {
      params: { interval, ...buParams(businessUnitIds) },
    });

    return res.data.map((item) => CollectionsSummaryLine.fromJson(item));
  }

  async getCollectionsSummaryByCustomer(
    interval?: AnalyticsInterval,
    businessUnitIds?: string[],
  ): Promise<CollectionsSummaryByCustomerLine[]> {
    const res = await this.api.get(this.path('collections/summary-by-customer'), {
      params: { interval, ...buParams(businessUnitIds) },
    });

    return res.data.map((item) => CollectionsSummaryByCustomerLine.fromJson(item));
  }

  async getOutreachSummary(
    interval: AnalyticsInterval,
    businessUnitIds?: string[],
  ): Promise<OutreachSummaryLine[]> {
    const res = await this.api.get(this.path('outreach/summary'), {
      params: { interval, ...buParams(businessUnitIds) },
    });

    return res.data.map((item) => OutreachSummaryLine.fromJson(item));
  }

  async getTaskTotals(businessUnitIds?: string[]): Promise<{
    type: TaskTypeTotal[];
    status: TaskStatusTotal[];
  }> {
    const res = await this.api.get(this.path('tasks/totals'), {
      params: { ...buParams(businessUnitIds) },
    });

    return res.data;
  }

  async getCustomerForecast(businessUnitIds?: string[]): Promise<CustomerForecastLine[]> {
    const res = await this.api.get(this.path('collections/customer-forecast'), {
      params: { days_threshold: 30, ...buParams(businessUnitIds) },
    });

    return res.data.map((item: any) => CustomerForecastLine.fromJson(item));
  }

  async getInvoiceForecast(
    daysThreshold: number = 7,
    businessUnitIds?: string[],
  ): Promise<InvoiceForecast> {
    const res = await this.api.get(this.path('collections/invoice-forecast'), {
      params: { days_threshold: daysThreshold, ...buParams(businessUnitIds) },
    });

    return res.data;
  }

  async getTaskSummary(
    interval?: AnalyticsInterval,
    businessUnitIds?: string[],
  ): Promise<TaskSummaryLine[]> {
    const res = await this.api.get(this.path('tasks/summary'), {
      params: { interval, ...buParams(businessUnitIds) },
    });

    return res.data.map((item: any) => TaskSummaryLine.fromJson(item));
  }

  async getMonthlyAgingSnapshots(
    input: MonthlyAgingSnapshotsInput,
    businessUnitIds?: string[],
  ): Promise<MonthlyAgingSnapshotsOutput> {
    const res = await this.api.get(this.path('collections/monthly-aging-snapshots'), {
      params: {
        start_date: input.start_date,
        end_date: input.end_date,
        ...(input.assignee_id && { assignee_id: input.assignee_id }),
        ...buParams(businessUnitIds),
      },
    });

    return res.data;
  }

  async getTaskAutomationBreakdown(
    interval: AnalyticsInterval = 'monthly',
    businessUnitIds?: string[],
  ): Promise<TaskAutomationBreakdownLine[]> {
    const res = await this.api.get(this.path('tasks/automation-breakdown'), {
      params: { interval, ...buParams(businessUnitIds) },
    });
    return res.data.map((item: any) => TaskAutomationBreakdownLine.fromJson(item));
  }

  async getTaskCompletionTimeByType(
    interval: AnalyticsInterval = 'monthly',
    businessUnitIds?: string[],
  ): Promise<TaskCompletionTimeByTypeLine[]> {
    const res = await this.api.get(this.path('tasks/completion-time-by-type'), {
      params: { interval, ...buParams(businessUnitIds) },
    });

    return res.data.map((item: any) => TaskCompletionTimeByTypeLine.fromJson(item));
  }

  async getCollectorOutreachSummary(
    businessUnitIds?: string[],
  ): Promise<CollectorOutreachSummaryItem[]> {
    const res = await this.api.get(this.path('collectors/outreach-summary'), {
      params: { ...buParams(businessUnitIds) },
    });

    return res.data.data;
  }
}
